import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Hint } from '@systemeio/ui'
import CourseActions from 'modules/courses/bundles/course-bundle/components/course-actions'
import {
  CourseBundleInterface,
  CourseBundleItemInterface,
} from 'modules/courses/bundles/course-bundle/types/course-bundle-interface'
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props'
import { getCourseCurriculumUrl } from 'modules/courses/courses/utils/get-course-curriculum-url'
import React from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { KeyedMutator } from 'swr'

interface CourseProps {
  item: CourseBundleItemInterface
  mutate: KeyedMutator<CourseBundleInterface>
}

const animateLayoutChanges: AnimateLayoutChanges = args =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true })

function Course({ item, mutate }: CourseProps) {
  const { t } = useLocoTranslation()
  const { attributes, isDragging, listeners, setNodeRef, transition, transform } = useSortable({
    id: item.id,
    animateLayoutChanges,
  })

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <div
      className="hover:border-brand-primary main-transition-colors bg-background-secondary mt-5 flex cursor-move flex-col gap-3 rounded-lg border border-transparent first:mt-0 focus-visible:outline-hidden"
      ref={setNodeRef}
      style={{ ...style }}
      {...attributes}
      {...listeners}
    >
      <div className="z-0 overflow-hidden rounded-lg">
        <div className="flex flex-col overflow-hidden rounded-b-lg lg:flex-row lg:items-center">
          <div className="text-text-primary text-start flex-[65_1_0%] truncate px-4 py-2 text-base lg:px-6 lg:py-4">
            <div
              className="relative flex h-full w-full max-w-fit cursor-pointer items-center gap-2 truncate text-base"
              {...nonDraggableProps}
            >
              <LinkWithoutPrefetch
                className="primary-link w-full truncate"
                href={getCourseCurriculumUrl({ courseId: item.course.id })}
              >
                {item.course.name}
              </LinkWithoutPrefetch>
            </div>
          </div>
          <div className="text-text-primary text-start flex-[25_1_0%] truncate px-4 py-2 text-base lg:px-6 lg:py-4">
            <div>
              <div
                className="relative flex h-full w-full max-w-fit items-center truncate"
                {...nonDraggableProps}
              >
                <span className="cursor-default truncate">
                  <Hint label={t('dashboard.course_bundle.course.delay_title_tooltip')}>
                    {t('dashboard.course_bundle.course.delay_title')} {item.delayBeforeStart || 0}
                  </Hint>
                </span>
              </div>
            </div>
          </div>
          <div className="text-text-primary font-inter flex-[10_1_0%] truncate px-4 py-2 text-[17px] font-bold lg:px-6 lg:py-4">
            <CourseActions item={item} mutate={mutate} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Course
